import { useState, useRef } from "react";
import { FileCheck, ChevronLeft, Download, Eye, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";
import { toTitleCase } from "@/lib/utils";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

export default function ConsentAndPreview({ profile, profileId, updateProfile, onPrev, loading }) {
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const pdfRef = useRef(null);

  const handleConsentChange = async (checked) => {
    setConsentAccepted(checked);
    setSubmitting(true);
    
    try {
      await updateProfile({
        consent_signed: checked,
        consent_date: checked ? new Date().toISOString() : null,
      });
      
      if (checked) {
        toast.success("Consentimiento Aceptado");
        await sendEmail();
      } else {
        setEmailSent(false);
      }
    } catch (error) {
      console.error("Error updating consent:", error);
      toast.error("Error Al Actualizar El Consentimiento");
      setConsentAccepted(!checked);
    } finally {
      setSubmitting(false);
    }
  };

  const buildCvPdf = async () => {
    if (!pdfRef.current) return null;
    const canvas = await html2canvas(pdfRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 10;

    pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    return pdf;
  };

  const buildCvPdfBase64 = async () => {
    const pdf = await buildCvPdf();
    if (!pdf) return null;
    const dataUri = pdf.output("datauristring");
    return dataUri.split(",")[1];
  };

  const sendEmail = async () => {
    if (emailSending || emailSent) return;
    if (!profile?.personal_data?.email) {
      toast.error("No Hay Correo Registrado");
      return;
    }

    setEmailSending(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      const cvBase64 = await buildCvPdfBase64();
      if (!cvBase64) {
        throw new Error("No PDF");
      }
      const fullName = `${profile.personal_data.first_name || ""} ${profile.personal_data.last_name || ""}`.trim() || "Usuario";

      await axios.post(`${API}/email/cv`, {
        email: profile.personal_data.email,
        full_name: fullName,
        cedula: profile.personal_data.cedula || "",
        cv_pdf_base64: cvBase64,
        consent_date: new Date().toISOString(),
      });

      setEmailSent(true);
      toast.success("Correo Enviado");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("No Se Pudo Enviar El Correo");
    } finally {
      setEmailSending(false);
    }
  };

  const handleFinishAcknowledgement = () => {
    // Try to close the tab first; if browser blocks it, leave to a blank page.
    window.open("", "_self");
    window.close();
    setTimeout(() => {
      window.location.replace("about:blank");
    }, 100);
  };

  const generatePDF = async () => {
    if (!pdfRef.current) return;

    setGeneratingPdf(true);
    try {
      const pdf = await buildCvPdf();
      if (!pdf) {
        throw new Error("No PDF");
      }
      const fileName = profile?.personal_data
        ? `CV_${profile.personal_data.first_name}_${profile.personal_data.last_name}.pdf`
        : "CV.pdf";

      pdf.save(fileName);
      toast.success("CV Descargado Correctamente");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error Al Generar El PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const formatBirthDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const formatted = format(new Date(dateStr), "d 'De' MMMM 'De' yyyy", { locale: es });
      return toTitleCase(formatted);
    } catch {
      return dateStr;
    }
  };

  const formatBirthLocation = (personalData) => {
    if (!personalData) return "";
    const parts = [
      personalData.birth_municipality,
      personalData.birth_city,
      personalData.birth_country,
    ].filter(Boolean);
    if (parts.length) return parts.join(", ");
    return personalData.birth_place || "";
  };

  const formatResidence = (personalData) => {
    if (!personalData) return "";
    const parts = [
      personalData.residence_municipality,
      personalData.residence_city,
      personalData.residence_country,
    ].filter(Boolean);
    if (parts.length) return parts.join(", ");
    return personalData.residence || "";
  };

  const formatWorkDate = (dateStr) => {
    if (!dateStr) return "Presente";
    const [year, month] = dateStr.split("-");
    const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const sortAcademicInfo = (items = []) =>
    [...items].sort((a, b) => {
      const yearA = parseInt(a.year, 10) || 0;
      const yearB = parseInt(b.year, 10) || 0;
      return yearB - yearA;
    });

  const sortWorkExperience = (items = []) => {
    const toDate = (value) => {
      if (!value) return null;
      const [year, month] = value.split("-");
      if (!year || !month) return null;
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    };
    return [...items].sort((a, b) => {
      if (a.current && !b.current) return -1;
      if (!a.current && b.current) return 1;
      const dateA = toDate(a.end_date || a.start_date);
      const dateB = toDate(b.end_date || b.start_date);
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      return timeB - timeA;
    });
  };

  const getLevelLabel = (value) => {
    const levels = {
      tecnica: "Técnica",
      tecnologica: "Tecnológica",
      universitaria: "Universitaria",
      especializacion: "Especialización",
      maestria: "Maestría",
      doctorado: "Doctorado",
      curso: "Curso / Certificación",
    };
    return levels[value] || value;
  };

  const experienceCategoryLabels = {
    "lider social": "Líder social",
    discapacidad: "Discapacidad",
    ddhh: "DDHH",
    "genero y poblacion diversa": "Género y población diversa",
    emprendimiento: "Emprendimiento",
    ambiental: "Ambiental",
    etnico: "Étnico",
    "justicia transicional victimas tierra": "Justicia transicional víctimas tierra",
    administracion: "Administración",
    "infraestructura-vivienda": "Infraestructura-vivienda",
    paz: "Paz",
    "grupos etareos": "Grupos etáreos",
    investigacion: "Investigación",
  };

  const formatExperienceCategories = (categories = []) => {
    if (!categories.length) return "";
    const formatted = categories.map(
      (value) => experienceCategoryLabels[value] || toTitleCase(value)
    );
    return formatted.join(", ");
  };

  const hasPersonalData = profile?.personal_data?.first_name;
  const hasAcademicInfo = profile?.academic_info?.length > 0;
  const hasWorkExperience = profile?.work_experience?.length > 0;
  const sortedAcademicInfo = sortAcademicInfo(profile?.academic_info || []);
  const sortedWorkExperience = sortWorkExperience(profile?.work_experience || []);
  const birthSummary = formatBirthLocation(profile?.personal_data);
  const residenceSummary = formatResidence(profile?.personal_data);

  return (
    <div data-testid="consent-preview-form">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-yellow-400 rounded flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-black" />
          </div>
          <h2 className="font-oswald text-2xl lg:text-3xl font-bold text-black tracking-wide">
            Consentimiento Y CV Final
          </h2>
        </div>
        <p className="text-gray-500 ml-13">Revise Y Descargue Su Hoja De Vida</p>
      </div>

      {/* Consent Section */}
      <Card className="border border-gray-200 rounded-sm mb-6">
        <CardContent className="p-6">
          <h3 className="font-oswald text-lg font-semibold mb-4">
            Tratamiento De Datos Personales
          </h3>
          <div className="bg-gray-50 p-4 rounded-sm mb-4 max-h-48 overflow-y-auto text-sm text-gray-700 leading-relaxed">
            <p className="mb-3">
              Al Aceptar Este Consentimiento, Usted Autoriza El Tratamiento De Sus Datos Personales
              De Acuerdo Con La Ley De Protección De Datos Personales Vigente En Su País.
            </p>
            <p className="mb-3">
              Sus Datos Serán Utilizados Exclusivamente Para:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1">
              <li>Generación De Su Hoja De Vida Profesional</li>
              <li>Almacenamiento Seguro De Su Información</li>
              <li>Facilitación De Procesos De Selección Laboral</li>
            </ul>
            <p className="mb-3">
              Usted Tiene Derecho A Acceder, Rectificar, Cancelar U Oponerse Al Tratamiento
              De Sus Datos En Cualquier Momento.
            </p>
            <p>
              Los Datos Proporcionados Serán Tratados Con Estricta Confidencialidad Y No
              Serán Compartidos Con Terceros Sin Su Autorización Expresa.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="consent"
              checked={consentAccepted}
              onCheckedChange={handleConsentChange}
              disabled={submitting}
              className="mt-1"
              data-testid="consent-checkbox"
            />
            <Label htmlFor="consent" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
              He Leído Y Acepto Los Términos Del Tratamiento De Datos Personales. Autorizo El Uso
              De Mi Información Para Los Fines Descritos Anteriormente.
            </Label>
          </div>
          {consentAccepted && profile?.consent_date && (
            <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
              <Check className="w-4 h-4" />
              Consentimiento Aceptado El {toTitleCase(
                format(new Date(profile.consent_date), "d 'De' MMMM 'De' yyyy 'A Las' HH:mm", { locale: es })
              )}
            </p>
          )}
          {consentAccepted && profile?.personal_data?.email && (emailSending || emailSent) && (
            <p className="text-xs text-gray-500 mt-2">
              {emailSending ? "Enviando Correo..." : `Correo Enviado A ${profile.personal_data.email}`}
            </p>
          )}
        </CardContent>
      </Card>

      {/* CV Preview Toggle */}
      <div className="flex gap-3 mb-6">
        <Button
          type="button"
          variant={showPreview ? "default" : "outline"}
          onClick={() => setShowPreview(!showPreview)}
          className={`flex-1 h-12 rounded-sm tracking-wide ${
            showPreview ? "bg-black text-yellow-400" : ""
          }`}
          data-testid="toggle-preview-button"
        >
          <Eye className="w-5 h-5 mr-2" />
          {showPreview ? "Ocultar Vista Previa" : "Ver Vista Previa"}
        </Button>
        <Button
          type="button"
          onClick={generatePDF}
          disabled={!consentAccepted || generatingPdf || !hasPersonalData}
          className="flex-1 bg-yellow-400 text-black hover:bg-yellow-500 h-12 rounded-sm tracking-wide font-bold disabled:opacity-50"
          data-testid="download-pdf-button"
        >
          {generatingPdf ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Download className="w-5 h-5 mr-2" />
              Descargar PDF
            </>
          )}
        </Button>
      </div>

      {/* CV Preview */}
      <div
        className={showPreview ? "" : "absolute -left-[9999px] -top-[9999px]"}
        aria-hidden={!showPreview}
      >
        <Card className="border border-gray-200 rounded-sm mb-6 overflow-hidden">
          <CardContent className="p-0">
              <div 
                ref={pdfRef} 
              className="cv-preview bg-white p-8"
              style={{ minHeight: "800px" }}
              data-testid="cv-preview"
            >
              {/* Header */}
              {hasPersonalData && (
                <div className="flex gap-6 mb-6">
                  {profile.personal_data.photo_url && (
                    <img
                      src={profile.personal_data.photo_url}
                      alt="Foto"
                      className="w-28 h-28 rounded object-cover border-2 border-gray-200"
                      crossOrigin="anonymous"
                    />
                  )}
                  <div className="flex-1">
                    <h1>
                      {profile.personal_data.first_name} {profile.personal_data.last_name}
                    </h1>
                    <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Cédula:</strong> {profile.personal_data.cedula}</p>
                  <p><strong>Fecha De Nacimiento:</strong> {formatBirthDate(profile.personal_data.birth_date)}</p>
                  {birthSummary && (
                    <p><strong>Lugar De Nacimiento:</strong> {birthSummary}</p>
                  )}
                  {profile.personal_data.email && (
                    <p><strong>Correo Electrónico:</strong> {profile.personal_data.email}</p>
                  )}
                  {profile.personal_data.phone && (
                    <p><strong>Teléfono Celular:</strong> {profile.personal_data.phone}</p>
                  )}
                  {residenceSummary && (
                    <p><strong>Residencia:</strong> {residenceSummary}</p>
                  )}
                      <p><strong>Nacionalidad:</strong> {profile.personal_data.nationality}</p>
                    </div>
                  </div>
                </div>
              )}

              {!hasPersonalData && (
                <div className="text-center py-8 text-gray-400">
                  <p>Complete Los Datos Personales Para Ver La Vista Previa</p>
                </div>
              )}

              {/* Academic Info */}
              {hasAcademicInfo && (
                <div className="cv-section">
                  <h2>Formación Académica</h2>
                  <div className="space-y-3">
                    {sortedAcademicInfo.map((entry) => (
                      <div key={entry.id} className="pl-4 border-l-2 border-yellow-400">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-gray-900">{entry.title}</p>
                            {entry.institution && (
                              <p className="text-sm text-gray-600">{entry.institution}</p>
                            )}
                            <p className="text-xs text-yellow-600 font-medium">
                              {getLevelLabel(entry.level)}
                            </p>
                          </div>
                          <span className="text-sm text-gray-500">{entry.year}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Experience */}
              {hasWorkExperience && (
                <div className="cv-section">
                  <h2>Experiencia Laboral</h2>
                  <div className="space-y-4">
                    {sortedWorkExperience.map((entry) => (
                      <div key={entry.id} className="pl-4 border-l-2 border-yellow-400">
                        <div className="flex justify-between items-start mb-1">
                          <div>
                            <p className="font-semibold text-gray-900">{entry.position}</p>
                            <p className="text-sm text-yellow-600 font-medium">{entry.company}</p>
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatWorkDate(entry.start_date)} - {entry.current ? "Presente" : formatWorkDate(entry.end_date)}
                          </span>
                        </div>
                        {entry.experience_categories?.length ? (
                          <p className="text-sm text-gray-600">
                            Categorías: {formatExperienceCategories(entry.experience_categories)}
                          </p>
                        ) : entry.description ? (
                          <p className="text-sm text-gray-600">{entry.description}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              {consentAccepted && (
                <div className="mt-8 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-400 text-center">
                    Documento Generado El {toTitleCase(
                      format(new Date(), "d 'De' MMMM 'De' yyyy", { locale: es })
                    )}
                    {" | "}Autorizo El Tratamiento De Mis Datos Personales
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onPrev}
          className="h-12 px-6 rounded-sm tracking-wide"
          data-testid="consent-prev-button"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Anterior
        </Button>
        <div className="text-right">
          {!consentAccepted && (
            <p className="text-sm text-gray-500 mb-2">
              Acepte El Consentimiento Para Descargar El CV
            </p>
          )}
          {consentAccepted && hasPersonalData && (
            <p className="text-sm text-green-600 flex items-center justify-end gap-1">
              <Check className="w-4 h-4" />
              Listo Para Descargar
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-6">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              disabled={!consentAccepted || !hasPersonalData}
              className="bg-yellow-400 text-black hover:bg-yellow-500 h-12 rounded-sm tracking-wide font-bold disabled:opacity-50"
              data-testid="finish-registration-button"
            >
              Finalizar Registro
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Registro Completado</AlertDialogTitle>
              <AlertDialogDescription>
                Sus datos han sido registrados exitosamente en la base de datos de la fundación y
                desde ahora estará en lista para futuras contrataciones. Gracias por realizar su registro.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogAction onClick={handleFinishAcknowledgement}>
              Entendido
            </AlertDialogAction>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
